import { createSignal, createResource, createMemo, For, Show, type JSX } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import PaymentsNav from '../components/PaymentsNav';
import PlayerHeadAvatar from '../components/PlayerHeadAvatar';
import PaymentEntryModal from '../components/payments/PaymentEntryModal';
import ScheduledPaymentEditor from '../components/payments/ScheduledPaymentEditor';
import { Badge } from '../components/Badge';
import { RelativeTime } from '../components/RelativeTime';
import { DateRangePills } from '../components/DateRangePills';
import { showConfirm } from '../components/Dialog';
import { useToast } from '../components/MessageBox';
import { describeSchedule, parseSchedule } from '../components/ScheduleEditor';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { formatCurrencyExact } from '../services/numberFormat';
import { usePaymentChanges } from '../hooks/usePaymentChanges';
import { useRelativeTimeTick } from '../services/relativeTime';
import type { ScheduledPaymentDto } from '../api';
import sellbot from './Sellbot.module.css';
import styles from '../components/payments/Payments.module.css';

type StateFilter = 'active' | 'paused' | 'done';

const STATE_PILLS = [
    { value: 'active' as StateFilter, labelKey: 'PAYMENTS_STATE_FILTER_ACTIVE' },
    { value: 'paused' as StateFilter, labelKey: 'PAYMENTS_STATE_FILTER_PAUSED' },
    { value: 'done' as StateFilter, labelKey: 'PAYMENTS_STATE_FILTER_DONE' },
];

const WAIT_ICON: Record<string, string> = {
    BotOffline: 'fa-plug-circle-xmark',
    BotControlled: 'fa-gamepad',
    PlayerOffline: 'fa-user-clock',
    OnlineListUnavailable: 'fa-users-slash',
    BalanceTooLow: 'fa-wallet',
    NeedsReview: 'fa-triangle-exclamation',
    RetryBackoff: 'fa-rotate',
    Sending: 'fa-paper-plane',
    FeatureDisabled: 'fa-lock',
    NotDue: 'fa-clock',
};

const SKELETON_CARD_COUNT = 3;

export default function PaymentsScheduled() {
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { botId } = useActiveBot();

    const [stateFilter, setStateFilter] = createSignal<StateFilter>('active');
    const [editorOpen, setEditorOpen] = createSignal(false);
    const [editing, setEditing] = createSignal<ScheduledPaymentDto | null>(null);
    const [reviewEntryId, setReviewEntryId] = createSignal<string | null>(null);
    const [busyId, setBusyId] = createSignal<string | null>(null);

    const [payments, { refetch }] = createResource(
        () => botId() || false,
        async (id) => {
            const res = await api.payments.scheduled(id);
            if (isOk(res.data)) return res.data.value as ScheduledPaymentDto[];
            toast?.error(firstError(res.data) ?? t('PAYMENTS_TOAST_LOAD_FAILED'));
            return [];
        },
    );

    usePaymentChanges(botId, () => void refetch());

    const now = useRelativeTimeTick();
    const isDue = (payment: ScheduledPaymentDto) => !!payment.nextDueUtc && new Date(payment.nextDueUtc).getTime() <= now();

    const sections = createMemo(() => {
        const all = payments.latest ?? [];
        switch (stateFilter()) {
            case 'paused': return [{ key: 'PAYMENTS_SECTION_PAUSED', icon: 'fa-pause', items: all.filter(p => p.state === 'Paused') }];
            case 'done': return [{ key: 'PAYMENTS_SECTION_DONE', icon: 'fa-check', items: all.filter(p => p.state === 'Completed') }];
            default: {
                const active = all.filter(p => p.state === 'Active');
                return [
                    { key: 'PAYMENTS_SECTION_REVIEW', icon: 'fa-triangle-exclamation', items: active.filter(p => p.reviewEntryId) },
                    { key: 'PAYMENTS_SECTION_DUE', icon: 'fa-hourglass-half', items: active.filter(p => !p.reviewEntryId && isDue(p)) },
                    { key: 'PAYMENTS_SECTION_UPCOMING', icon: 'fa-calendar-days', items: active.filter(p => !p.reviewEntryId && !isDue(p)) },
                ];
            }
        }
    });

    const visibleCount = () => sections().reduce((count, section) => count + section.items.length, 0);

    const cadence = (payment: ScheduledPaymentDto) => payment.kind === 'Recurring' && payment.scheduleJson
        ? describeSchedule(parseSchedule(payment.scheduleJson), t)
        : t('PAYMENTS_ONCE_DUE', formatDateTime(payment.dueAtUtc));

    const progress = (payment: ScheduledPaymentDto) => payment.maxPayments
        ? t('PAYMENTS_PROGRESS_OF', payment.paidCount, payment.maxPayments)
        : t('PAYMENTS_PROGRESS', payment.paidCount);

    type ScheduledActionResponse = Awaited<ReturnType<typeof api.payments.pauseScheduled>>;

    const run = async (payment: ScheduledPaymentDto, action: () => Promise<ScheduledActionResponse>, successKey: string) => {
        setBusyId(payment.id);
        const res = await action();
        setBusyId(null);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('PAYMENTS_TOAST_ACTION_FAILED'));
            return;
        }
        toast?.success(t(successKey, payment.playerName));
        void refetch();
    };

    const skip = async (payment: ScheduledPaymentDto) => {
        if (!await showConfirm(t('PAYMENTS_SKIP_CONFIRM', payment.playerName, formatDateTime(payment.nextDueUtc)))) return;
        await run(payment, () => api.payments.skipScheduled(botId()!, payment.id), 'PAYMENTS_TOAST_SKIPPED');
    };

    const remove = async (payment: ScheduledPaymentDto) => {
        if (!await showConfirm(t('PAYMENTS_DELETE_CONFIRM', payment.playerName), true)) return;
        await run(payment, () => api.payments.cancelScheduled(botId()!, payment.id), 'PAYMENTS_TOAST_DELETED');
    };

    const openEditor = (payment: ScheduledPaymentDto | null) => {
        setEditing(payment);
        setEditorOpen(true);
    };

    const waitLine = (payment: ScheduledPaymentDto): JSX.Element => {
        const reason = payment.reviewEntryId ? 'NeedsReview' : payment.waitingReason;
        if (!reason || (!isDue(payment) && reason !== 'NeedsReview')) return null;
        return (
            <span class={`${styles.waitLine} ${reason === 'NeedsReview' ? styles.waitWarning : ''}`}>
                <i class={`fa-solid ${WAIT_ICON[reason] ?? 'fa-clock'}`} /> {t(`PAYMENTS_WAIT_${reason.toUpperCase()}`, payment.playerName)}
            </span>
        );
    };

    return (
        <div class={sellbot.container}>
            <PaymentsNav />
            <div class={sellbot.headerRow}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1><Text val="PAYMENTS_SCHEDULED_TITLE" /></h1>
                    <span class={sellbot.heroSubtitle}><Text val="PAYMENTS_SCHEDULED_SUBTITLE" /></span>
                </div>
                <button type="button" class="btn btn-primary" disabled={!botId()} onClick={() => openEditor(null)}>
                    <i class="fa-solid fa-plus" /> <Text val="PAYMENTS_NEW" />
                </button>
            </div>

            <DateRangePills value={stateFilter()} onChange={setStateFilter} pills={STATE_PILLS} />

            <Show when={payments.state !== 'pending'} fallback={
                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                    <For each={Array.from({ length: SKELETON_CARD_COUNT })}>{() => <div class={`fcard ${styles.cardSkeleton}`} />}</For>
                </div>
            }>
                <Show when={visibleCount() > 0} fallback={
                    <div class={`fcard ${sellbot.empty}`}>
                        <i class="fa-solid fa-calendar-plus" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                        <p><Text val={stateFilter() === 'active' ? 'PAYMENTS_SCHEDULED_EMPTY' : 'PAYMENTS_SCHEDULED_EMPTY_FILTER'} /></p>
                        <Show when={stateFilter() === 'active'}>
                            <button type="button" class="btn btn-primary" onClick={() => openEditor(null)}>
                                <i class="fa-solid fa-plus" /> <Text val="PAYMENTS_NEW" />
                            </button>
                        </Show>
                    </div>
                }>
                    <For each={sections().filter(section => section.items.length > 0)}>
                        {(section) => (
                            <section class="v" style={{ gap: 'var(--spacing-sm)' }}>
                                <h2 class={styles.sectionTitle}><i class={`fa-solid ${section.icon}`} /> <Text val={section.key} /> <span class={styles.sectionCount}>{section.items.length}</span></h2>
                                <For each={section.items}>
                                    {(payment) => (
                                        <div class={`fcard ${styles.scheduleCard} ${payment.reviewEntryId ? styles.scheduleCardReview : ''}`}>
                                            <PlayerHeadAvatar
                                                name={payment.playerName}
                                                size={40}
                                                showStatus={payment.waitingReason === 'PlayerOffline'}
                                                online={false}
                                            />
                                            <div class={styles.scheduleMain}>
                                                <div class={styles.scheduleHeadline}>
                                                    <span class={styles.schedulePlayer}>{payment.playerName}</span>
                                                    <span class={styles.scheduleAmount}>{formatCurrencyExact(payment.amount)}</span>
                                                    <Badge tone={payment.kind === 'Recurring' ? 'accent' : 'info'} pill>
                                                        <Text val={payment.kind === 'Recurring' ? 'PAYMENTS_KIND_RECURRING' : 'PAYMENTS_KIND_ONCE'} />
                                                    </Badge>
                                                </div>
                                                <span class={styles.scheduleCadence}>{cadence(payment)}</span>
                                                <Show when={payment.reason}>
                                                    {(reason) => <span class={styles.muted} title={t('PAYMENTS_REASON')}><i class="fa-solid fa-quote-left" /> {reason()}</span>}
                                                </Show>
                                                <Show when={payment.note}>
                                                    {(note) => <span class={styles.muted}><i class="fa-solid fa-note-sticky" /> {note()}</span>}
                                                </Show>
                                                <div class={styles.scheduleMeta}>
                                                    <Show when={payment.nextDueUtc}>
                                                        {(next) => (
                                                            <span title={formatDateTime(next())}>
                                                                <i class="fa-solid fa-clock" /> <Text val={isDue(payment) ? 'PAYMENTS_DUE_SINCE' : 'PAYMENTS_NEXT_DUE'} />{' '}
                                                                <RelativeTime value={next()} />
                                                            </span>
                                                        )}
                                                    </Show>
                                                    <Show when={payment.kind === 'Recurring' || payment.paidCount > 0}>
                                                        <span><i class="fa-solid fa-receipt" /> {progress(payment)}</span>
                                                    </Show>
                                                    <Show when={payment.message}>
                                                        <span title={payment.message!}><i class="fa-solid fa-comment" /> <Text val="PAYMENTS_WITH_MESSAGE" /></span>
                                                    </Show>
                                                </div>
                                                {waitLine(payment)}
                                            </div>
                                            <div class={styles.scheduleActions}>
                                                <Show when={payment.reviewEntryId}>
                                                    {(entryId) => (
                                                        <button type="button" class="btn btn-primary btn-sm" onClick={() => setReviewEntryId(entryId())}>
                                                            <i class="fa-solid fa-magnifying-glass" /> <Text val="PAYMENTS_REVIEW" />
                                                        </button>
                                                    )}
                                                </Show>
                                                <Show when={payment.kind === 'Once' && payment.state === 'Active' && !isDue(payment)}>
                                                    <button type="button" class="btn btn-secondary btn-sm" disabled={busyId() === payment.id}
                                                        onClick={() => void run(payment, () => api.payments.payScheduledNow(botId()!, payment.id), 'PAYMENTS_TOAST_PAY_NOW')}>
                                                        <i class="fa-solid fa-bolt" /> <Text val="PAYMENTS_PAY_NOW" />
                                                    </button>
                                                </Show>
                                                <Show when={payment.state === 'Active' && payment.kind === 'Recurring' && !payment.reviewEntryId}>
                                                    <button type="button" class="btn btn-secondary btn-sm" disabled={busyId() === payment.id} onClick={() => void skip(payment)}>
                                                        <i class="fa-solid fa-forward" /> <Text val="PAYMENTS_SKIP" />
                                                    </button>
                                                </Show>
                                                <Show when={payment.state === 'Active'}>
                                                    <button type="button" class="btn btn-secondary btn-sm" disabled={busyId() === payment.id}
                                                        onClick={() => void run(payment, () => api.payments.pauseScheduled(botId()!, payment.id), 'PAYMENTS_TOAST_PAUSED')}>
                                                        <i class="fa-solid fa-pause" /> <Text val="PAYMENTS_PAUSE" />
                                                    </button>
                                                </Show>
                                                <Show when={payment.state === 'Paused'}>
                                                    <button type="button" class="btn btn-secondary btn-sm" disabled={busyId() === payment.id}
                                                        onClick={() => void run(payment, () => api.payments.resumeScheduled(botId()!, payment.id), 'PAYMENTS_TOAST_RESUMED')}>
                                                        <i class="fa-solid fa-play" /> <Text val="PAYMENTS_RESUME" />
                                                    </button>
                                                </Show>
                                                <button type="button" class="btn btn-secondary btn-sm btn-icon" title={t('PAYMENTS_SHOW_HISTORY')}
                                                    onClick={() => navigate(`/payments?player=${encodeURIComponent(payment.playerName)}`)}>
                                                    <i class="fa-solid fa-clock-rotate-left" />
                                                </button>
                                                <Show when={payment.state !== 'Completed'}>
                                                    <button type="button" class="btn btn-secondary btn-sm btn-icon" title={t('PAYMENTS_EDIT')} onClick={() => openEditor(payment)}>
                                                        <i class="fa-solid fa-pen-to-square" />
                                                    </button>
                                                </Show>
                                                <button type="button" class="btn btn-danger btn-sm btn-icon" title={t('PAYMENTS_DELETE')} disabled={busyId() === payment.id} onClick={() => void remove(payment)}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </For>
                            </section>
                        )}
                    </For>
                </Show>
            </Show>

            <Show when={botId()}>
                {(id) => (
                    <>
                        <ScheduledPaymentEditor
                            open={editorOpen()}
                            botId={id()}
                            editing={editing()}
                            onClose={() => setEditorOpen(false)}
                            onSaved={() => { setEditorOpen(false); void refetch(); }}
                        />
                        <PaymentEntryModal botId={id()} entryId={reviewEntryId()} onClose={() => setReviewEntryId(null)} onChanged={() => void refetch()} />
                    </>
                )}
            </Show>
        </div>
    );
}
